/**
 * shared/money.js
 *
 * The single arithmetic authority for money, imported by BOTH runtimes: Node
 * (server/**) and the browser (src/js/**, bundled by esbuild). Pure integer-pence
 * functions — NO imports, NO DOM, NO node builtins — so it runs unchanged in
 * either, and so scripts/test-settlement.mjs can exercise it in isolation.
 *
 * It lives here rather than under server/ because the compose screens have to
 * show the user the same figure the server will store, to the penny: instalment
 * validation compares them directly. That guarantee used to be a comment asking
 * two copies of this file to be kept "in lockstep" — src/js/lib/vat-bands.js was
 * byte-identical to the VAT banding below, and only the server copy was tested.
 * One module, imported twice, makes the drift impossible instead of policed.
 *
 * Everything is integer pence. The one tolerance below replaces the scattered
 * float 0.01 / 0.005 thresholds the codebase used to carry, so two payments of
 * 33.33 + 66.67 settle a 100.00 invoice cleanly instead of stranding it at a
 * fraction of a penny outstanding.
 */

// Settlement slack, in pence. An invoice is "fully paid" once cash + credit
// reaches within this of the total.
export const MONEY_TOLERANCE_PENCE = 1;

// The typed source of an invoice_payments row (migration 034). `synthetic` is
// the "mark as fully paid" top-up; `refund` rows carry a negative amount.
export const PAYMENT_METHODS = [
  "manual", // legacy typed-less recordings (pre-039)
  "stripe",
  "refund",
  "synthetic",
  // Typed manual methods (migration 039) — how clients actually pay
  // outside Stripe.
  "bank_transfer",
  "cash",
  "cheque",
  "other",
  // Stripe Pay by Bank / "Instant bank payment" (migration 056). Still a
  // Stripe settlement, but kept distinct from `stripe` (which means card):
  // the fees differ and the CSV export has to be able to tell them apart.
  "pay_by_bank"
];

// The Stripe-settled methods. Anything that looks up "the Stripe payment on
// this invoice" — refund matching above all — has to consider both, or a
// Pay-by-Bank payment silently becomes unrefundable.
export const STRIPE_PAYMENT_METHODS = ["stripe", "pay_by_bank"];

// Parse a NUMERIC(12,2) pounds value (string or number) into integer pence.
export function toPence(value) {
  const n = Number.parseFloat(String(value ?? "0"));
  if (!Number.isFinite(n)) return 0;
  return Math.round(n * 100);
}

// Render integer pence back to a NUMERIC(12,2)-compatible pounds string.
export function fromPence(pence) {
  return (Number(pence) / 100).toFixed(2);
}

// Outstanding balance in pence: total − cash paid − credit applied − CIS
// deduction, floored at 0. Accepts a raw invoice row (snake_case NUMERIC
// columns). The CIS deduction (migration 042) is money the contractor pays
// to HMRC on the subcontractor's behalf, so it settles the invoice without
// ever arriving as cash — same treatment as a credit note. A caller whose
// SELECT omits cis_deduction_amount fails safe: outstanding reads too high,
// never too low.
export function computeOutstandingPence(invoiceRow) {
  return outstandingPenceOf({
    totalPence: toPence(invoiceRow.total_amount),
    paidPence: toPence(invoiceRow.amount_paid),
    creditedPence: toPence(invoiceRow.amount_credited),
    cisDeductionPence: toPence(invoiceRow.cis_deduction_amount)
  });
}

/**
 * The same sum from figures already in pence, which is how the API sends them
 * (`…Pence` on every serialized document). The web reads these rather than the
 * pounds twins, so no balance is worked out from a float.
 */
export function outstandingPenceOf({ totalPence, paidPence, creditedPence, cisDeductionPence } = {}) {
  const n = (value) => (Number.isFinite(Number(value)) ? Number(value) : 0);
  return Math.max(0, n(totalPence) - n(paidPence) - n(creditedPence) - n(cisDeductionPence));
}

// What has settled an invoice so far, kept apart, for a page the client reads.
// Cash, credit notes and a CIS deduction all reduce the balance (above), but
// only cash is money the client paid: a contractor withholds the CIS for HMRC,
// and a credit note is the sender taking something off. The public pay page
// once showed total − outstanding as "Paid", so a contractor who had paid
// nothing on a CIS invoice read that they'd paid the deduction. Accepts a raw
// invoice row, like computeOutstandingPence; each part floors at 0.
export function settlementBreakdown(invoiceRow) {
  return {
    paidPence: Math.max(0, toPence(invoiceRow.amount_paid)),
    creditedPence: Math.max(0, toPence(invoiceRow.amount_credited)),
    cisDeductionPence: Math.max(0, toPence(invoiceRow.cis_deduction_amount)),
    outstandingPence: computeOutstandingPence(invoiceRow)
  };
}

// CIS deduction in pence: rate % of the labour element (excl. VAT), clamped
// to [0, labour]. The invoice total is untouched — the deduction only reduces
// what the contractor pays in cash.
export function computeCisDeductionPence({ rate, labourPence }) {
  const r = Number.parseFloat(String(rate ?? ""));
  if (!Number.isFinite(r) || r <= 0 || labourPence <= 0) return 0;
  return Math.max(0, Math.min(Math.round((labourPence * r) / 100), labourPence));
}

/**
 * Derive the post-payment settlement state.
 *
 * `deltaPence` is the cash change: positive for a payment, negative for a
 * refund/reversal. `newPaidPence` is never clamped down — money that actually
 * moved is recorded even if it pushes past the total (`overpaidPence` surfaces
 * the excess); readers use `outstandingPence`, which floors at 0.
 *
 * Status keys on CASH for the partial-vs-sent distinction (a credit note is not
 * a payment, so a purely credited-but-unpaid invoice stays `sent`), while full
 * settlement counts cash + credit (so a credit note can complete an invoice).
 */
/**
 * Freeze a deposit into integer pence against the invoice total.
 *
 * `percent` → round(total × value/100); `fixed` → round(value × 100). The result
 * is clamped to [0, total] so a deposit can never exceed the invoice. The balance
 * is the total minus the deposit **by subtraction** — the two always reconcile to
 * the penny (never round both independently). The percentage is display metadata;
 * this pence amount is the single value money math reads. Returns null when there
 * is no deposit.
 */
/**
 * Compute the discount in pence from its type/value pair (migration 041).
 * Applied to the SUBTOTAL before VAT: net = subtotal − discount, tax is
 * charged on the net. Clamped to [0, subtotal] so a discount can never turn
 * an invoice negative. Returns 0 when there is no discount — callers can
 * always subtract it unconditionally.
 */
export function computeDiscountPence({ discountType, discountValue, subtotalPence }) {
  if (discountType !== "percent" && discountType !== "fixed") return 0;
  const value = Number.parseFloat(String(discountValue ?? ""));
  if (!Number.isFinite(value) || value <= 0) return 0;
  const raw = discountType === "percent"
    ? Math.round((subtotalPence * value) / 100)
    : Math.round(value * 100);
  return Math.max(0, Math.min(raw, subtotalPence));
}

/**
 * Group line items into VAT rate bands and compute the tax (migration 045).
 *
 * Each line's rate is its own `vatRate` when set, else `documentRate` — so an
 * invoice with no per-line rates collapses to one band at the document rate
 * and reproduces the pre-045 maths to the penny. A document discount is
 * allocated across lines proportionally by value BEFORE banding (largest-
 * remainder penny reconciliation, ties broken by line order), so the banded
 * nets always sum exactly to subtotal − discount. Band tax = round(net ×
 * rate/100) per band; `taxPence` is the band sum.
 *
 * Returns { bands: [{ rate, netPence, taxPence }] (rate-ascending),
 * taxPence }. Callers own the gating: pass documentRate 0 (and null line
 * rates) for non-GBP documents, and ignore taxPence under reverse charge
 * (it is the "would-be" figure for the document note).
 */
export function computeVatBands({ lines, documentRate, discountPence = 0 }) {
  const docRate = Number.parseFloat(String(documentRate ?? ""));
  const safeDocRate = Number.isFinite(docRate) && docRate > 0 ? docRate : 0;
  const parsed = (Array.isArray(lines) ? lines : [])
    .map((line) => {
      const linePence = Math.max(0, Math.round(Number(line?.linePence) || 0));
      const raw = line?.vatRate;
      const own = raw == null || String(raw).trim() === "" ? null : Number.parseFloat(String(raw));
      const rate = own != null && Number.isFinite(own) && own >= 0 ? own : safeDocRate;
      return { linePence, rate };
    });

  const subtotalPence = parsed.reduce((sum, l) => sum + l.linePence, 0);
  const discount = Math.max(0, Math.min(Math.round(Number(discountPence) || 0), subtotalPence));

  // Proportional discount allocation, penny-reconciled (largest remainder).
  let discounted = parsed.map((l) => ({ ...l, netPence: l.linePence }));
  if (discount > 0 && subtotalPence > 0) {
    const shares = parsed.map((l) => (l.linePence * discount) / subtotalPence);
    const floors = shares.map((s) => Math.floor(s));
    let remainder = discount - floors.reduce((sum, f) => sum + f, 0);
    const order = shares
      .map((s, i) => ({ i, frac: s - Math.floor(s) }))
      .sort((a, b) => b.frac - a.frac || a.i - b.i);
    const extra = parsed.map(() => 0);
    for (const { i } of order) {
      if (remainder <= 0) break;
      extra[i] = 1;
      remainder -= 1;
    }
    discounted = parsed.map((l, i) => ({ ...l, netPence: l.linePence - floors[i] - extra[i] }));
  }

  const byRate = new Map();
  for (const l of discounted) {
    byRate.set(l.rate, (byRate.get(l.rate) || 0) + l.netPence);
  }
  const bands = [...byRate.entries()]
    .sort((a, b) => a[0] - b[0])
    .map(([rate, netPence]) => ({ rate, netPence, taxPence: Math.round((netPence * rate) / 100) }));

  return { bands, taxPence: bands.reduce((sum, b) => sum + b.taxPence, 0) };
}

export function computeDeposit({ depositType, depositValue, totalPence }) {
  if (depositType !== "percent" && depositType !== "fixed") return null;
  const value = Number.parseFloat(String(depositValue ?? ""));
  if (!Number.isFinite(value) || value <= 0) return null;
  const raw = depositType === "percent"
    ? Math.round((totalPence * value) / 100)
    : Math.round(value * 100);
  const depositPence = Math.max(0, Math.min(raw, totalPence));
  return { depositPence, balancePence: totalPence - depositPence };
}

export function computeSettlement({ totalPence, currentPaidPence, creditedPence = 0, cisDeductionPence = 0, deltaPence = 0 }) {
  // CIS sits on the non-cash side with credit: it completes settlement but
  // never flips an invoice to partially_paid on its own.
  const nonCashPence = creditedPence + cisDeductionPence;
  const newPaidPence = Math.max(0, currentPaidPence + deltaPence);
  const fullyPaid = newPaidPence + nonCashPence >= totalPence - MONEY_TOLERANCE_PENCE;
  const outstandingPence = Math.max(0, totalPence - newPaidPence - nonCashPence);
  const overpaidPence = Math.max(0, newPaidPence + nonCashPence - totalPence);

  let newStatus;
  if (fullyPaid) newStatus = "paid";
  else if (newPaidPence > MONEY_TOLERANCE_PENCE) newStatus = "partially_paid";
  else newStatus = "sent";

  return { newPaidPence, outstandingPence, overpaidPence, fullyPaid, newStatus };
}

// Allocate settled money (cash + credit, in pence) across ordered instalments,
// oldest-first: an instalment is "paid" once the running total covers it. Pure.
// Returns the set of paid instalment ids and the seq of the first unpaid one.
export function allocateInstalments(instalments, settledPence) {
  const ordered = [...instalments].sort((a, b) => a.seq - b.seq);
  const paidIds = new Set();
  let running = 0;
  let nextDueSeq = null;
  for (const ins of ordered) {
    running += Number(ins.amount_pence);
    if (running <= settledPence + MONEY_TOLERANCE_PENCE) {
      paidIds.add(ins.id);
    } else if (nextDueSeq === null) {
      nextDueSeq = ins.seq;
    }
  }
  return { paidIds, nextDueSeq };
}

// ---------------------------------------------------------------------------
// What an invoice is waiting on, and when it was due.
//
// An invoice has ONE date in the database, but it can be waiting on something
// else entirely. Three mutually exclusive phases, in precedence order:
//
//   instalment  a milestone plan chases its next unpaid instalment, anchored to
//               that instalment's own due date, re-anchoring as each is paid
//   deposit     an unpaid deposit with its own due date is chased first
//   balance     everything else is the invoice's own due date
//
// This lived inside server/reminders.js, where the reminder engine had it right
// while everything that labelled an invoice "overdue" read due_date alone. So a
// six-month plan on a 30-day invoice was chased correctly per instalment and
// still turned red in the list after 30 days, with nothing late. One function,
// so the chaser and the label cannot disagree again.
//
// Returns null when there is nothing left to chase — every instalment covered.
// Deposits and plans are mutually exclusive on an invoice, so the instalment
// branch takes precedence cleanly.
export function nextAmountDue({
  dueDate = null,
  instalments = [],
  settledPence = 0,
  paidPence = 0,
  depositAmountPence = 0,
  depositDueDate = null
} = {}) {
  if (Array.isArray(instalments) && instalments.length > 0) {
    const { nextDueSeq } = allocateInstalments(instalments, settledPence);
    if (nextDueSeq == null) return null;
    const target = [...instalments].find((ins) => ins.seq === nextDueSeq);
    return { phase: "instalment", dueDate: target?.due_date ?? null, instalmentSeq: nextDueSeq };
  }
  const depositPence = Number(depositAmountPence) || 0;
  if (depositPence > 0 && Number(paidPence) < depositPence && depositDueDate) {
    return { phase: "deposit", dueDate: depositDueDate, instalmentSeq: null };
  }
  return { phase: "balance", dueDate, instalmentSeq: null };
}

// ---------------------------------------------------------------------------
// Refundable cash on an invoice.
//
// "Refundable" is narrower than "paid": only money Stripe actually collected
// can be sent back through Stripe. Cash, cheque and bank-transfer payments are
// recorded here too, but refunding those happens outside Sendinvo — that's
// what the separate "Record refund" control is for.
//
// Both Stripe-settled methods count: a Pay by Bank payment refunds to the
// payer's bank account for up to two years after it settles.
//
// The single definition of this rule. It backs both the POST that performs a
// refund and the GET the drawer uses to decide whether to offer one, so the
// button can't appear for an amount the endpoint would then reject.
// ---------------------------------------------------------------------------

export const REFUNDABLE_METHODS = ["stripe", "pay_by_bank"];

export function computeRefundable(paymentRows) {
  let collectedPence = 0;
  let refundedPence = 0;
  let paymentIntentId = null;
  for (const row of paymentRows || []) {
    const pence = Math.round(Number.parseFloat(String(row.amount || 0)) * 100);
    if (REFUNDABLE_METHODS.includes(row.method)) {
      collectedPence += pence;
      if (row.stripe_payment_intent_id) paymentIntentId = row.stripe_payment_intent_id;
    } else if (row.method === "refund") {
      // Refunds are stored negative; count them as positive already-returned.
      refundedPence += Math.abs(pence);
    }
  }
  // Never negative: a refund recorded by hand against a Stripe payment could
  // otherwise push this below zero and read as "owed back to you".
  const refundablePence = Math.max(0, collectedPence - refundedPence);
  return { refundablePence, paymentIntentId, collectedPence, refundedPence };
}

// The document-level VAT rate a saved invoice was composed at.
//
// It is never stored. With per-line VAT off every item's vat_rate is NULL and
// the rate survives only inside tax_amount, so re-opening a saved invoice has
// to work it back out. Returns null when it genuinely can't be known (nothing
// taxable to divide by), which the compose screens read as "use the company
// default" rather than inventing a number.
//
// Order matters:
//  - Reverse charge first: tax_amount is 0 by definition there, and the rate
//    the document quotes lives in its own column.
//  - Then explicit per-line rates. computeVatBands' callers freeze the
//    document rate onto every null line as soon as ONE line is explicit, so
//    these are all-or-nothing. A single shared value IS the document rate; a
//    genuine mix has no single answer, so take the largest — it's the one a
//    new line should default to, and the compose screen shows each line's own
//    rate anyway.
//  - Otherwise divide the tax by what it was charged on.
export function documentVatRateFromTotals({
  subtotalPence = 0,
  discountPence = 0,
  taxPence = 0,
  reverseCharge = false,
  reverseChargeRate = null,
  lineRates = []
} = {}) {
  if (reverseCharge) {
    const rc = Number.parseFloat(String(reverseChargeRate ?? ""));
    return Number.isFinite(rc) ? rc : null;
  }

  const explicit = lineRates
    .map((r) => Number.parseFloat(String(r ?? "")))
    .filter((r) => Number.isFinite(r));
  if (explicit.length) return Math.max(...explicit);

  const taxable = Math.round(subtotalPence) - Math.round(discountPence);
  if (taxable <= 0) return null;
  // Two decimals, matching NUMERIC(5,2) — so a rate read back equals the rate
  // that was stored, and the select finds it among its known options.
  return Math.round((Math.round(taxPence) / taxable) * 10000) / 100;
}
